// Hook: obsadenosť z Google Kalendára + odvodené údaje
// (najbližší voľný termín, počet rezervovaných dní v sezóne).

import { useCallback, useEffect, useMemo, useState } from 'react';
import config from '../config';
import { loadOccupiedDays } from '../utils/ical';
import { addDays, fromKey, toKey } from '../utils/dates';

export default function useAvailability() {
  const [occupiedDays, setOccupiedDays] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const refresh = useCallback(async (force = false) => {
    try {
      const days = await loadOccupiedDays({ force });
      setOccupiedDays(days);
      setStatus('ready');
    } catch (error) {
      setStatus((prev) => (prev === 'ready' ? 'ready' : 'error'));
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(() => refresh(true), config.icalRefreshMinutes * 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  const occupiedSet = useMemo(() => new Set(occupiedDays), [occupiedDays]);

  // Najbližší dátum, od ktorého je voľných aspoň minDays dní po sebe.
  const nextAvailableKey = useMemo(() => {
    if (status !== 'ready') return null;
    let cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    const horizon = addDays(cursor, 365);
    while (cursor < horizon) {
      let free = true;
      for (let i = 0; i < config.minDays; i += 1) {
        if (occupiedSet.has(toKey(addDays(cursor, i)))) {
          free = false;
          break;
        }
      }
      if (free) return toKey(cursor);
      cursor = addDays(cursor, 1);
    }
    return null;
  }, [occupiedSet, status]);

  // Počet rezervovaných dní v aktuálnom kalendárnom roku (sociálny dôkaz).
  const bookedNightsThisSeason = useMemo(() => {
    const year = new Date().getFullYear();
    return occupiedDays.filter((key) => fromKey(key).getFullYear() === year).length;
  }, [occupiedDays]);

  return { occupiedSet, status, nextAvailableKey, bookedNightsThisSeason, refresh };
}
